import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Hero from "@/components/Hero";
import FreshPicks from "@/components/FreshPicks";
import TrustStrip from "@/components/TrustStrip";
import ProductCard from "@/components/ProductCard";
import Newsletter from "@/components/Newsletter";
import { fetchSection, mapShopifyProduct } from "@/lib/shopify";
import { WEAR_PRODUCTS, BIG_LIVING_PRODUCTS } from "@/lib/placeholderData";
import { TEST_IDS } from "@/constants/testIds";

function SectionPreview({ id, testId, title, subtitle, products, onShopAll }) {
  return (
    <section id={id} data-testid={testId} className="py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between mb-10 lg:mb-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-plum tracking-tight">
              {title}
            </h2>
            {subtitle && (
              <p className="mt-2 text-sm lg:text-base text-plum-light max-w-lg leading-relaxed">
                {subtitle}
              </p>
            )}
          </motion.div>

          <motion.button
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            onClick={onShopAll}
            className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-plum hover:text-amber transition-colors duration-200 shrink-0"
            data-testid={`${id}-shop-all`}
          >
            Shop all
            <ArrowRight className="w-4 h-4" />
          </motion.button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
          {products.slice(0, 4).map((product, i) => (
            <ProductCard key={product.id} product={product} index={i} />
          ))}
        </div>

        {/* Mobile shop all */}
        <div className="sm:hidden mt-8 text-center">
          <button
            onClick={onShopAll}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-plum hover:text-amber transition-colors"
          >
            Shop all
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const [wearProducts, setWearProducts] = useState(WEAR_PRODUCTS);
  const [bigLivingProducts, setBigLivingProducts] = useState(BIG_LIVING_PRODUCTS);

  useEffect(() => {
    let cancelled = false;
    async function loadProducts() {
      const [wearResult, livingResult] = await Promise.all([
        fetchSection("wear", 8),
        fetchSection("big-living", 8),
      ]);
      if (cancelled) return;
      if (wearResult.products?.length > 0) {
        setWearProducts(wearResult.products.map(mapShopifyProduct));
      }
      if (livingResult.products?.length > 0) {
        setBigLivingProducts(livingResult.products.map(mapShopifyProduct));
      }
    }
    loadProducts();
    return () => { cancelled = true; };
  }, []);

  const navigate = useNavigate();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <Hero />
      <FreshPicks />
      <TrustStrip />

      <SectionPreview
        id="wear"
        testId={TEST_IDS.SECTION.wear}
        title="Wear"
        subtitle="Clothing that fits your body and your life. Every piece is tried on, tested, and chosen by people who get it."
        products={wearProducts}
        onShopAll={() => navigate("/shop/wear")}
      />

      <SectionPreview
        id="big-living"
        testId={TEST_IDS.SECTION.bigLiving}
        title="Big Living"
        subtitle="Your home, your adventures, your life — without the worry. We checked."
        products={bigLivingProducts}
        onShopAll={() => navigate("/shop/big-living")}
      />

      {/* Our Standard — minimal brand block */}
      <section id="our-standard" data-testid="section-our-standard" className="py-20 lg:py-28 bg-plum">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber mb-4">
              Our Standard
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight">
              You've been left out of enough places<span className="text-amber">.</span>
            </h2>
            <p className="mt-3 text-xl font-serif italic text-white/80">Not this one.</p>
            <p className="mt-8 text-sm lg:text-base text-white/70 leading-relaxed max-w-xl mx-auto">
              Every product is tried, tested, and chosen by people who live in bigger bodies.
              No "flattering." No "slimming." Just good stuff that fits and feels great.
            </p>
          </motion.div>
        </div>
      </section>

      {/* About — simple info block */}
      <section id="about" data-testid="section-about" className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-2xl lg:text-3xl font-bold text-plum tracking-tight">
              About You Belong Club
            </h2>
            <p className="mt-6 text-base text-plum-light leading-relaxed">
              You Belong Club is a curated marketplace for bigger people. From the sofa you actually
              fit on to the jeans you'll want to live in — every product is checked for real comfort,
              real fit, and real joy. Because you deserve to shop without compromise.
            </p>
          </motion.div>
        </div>
      </section>

      <Newsletter />
    </>
  );
}
